import { Tag } from '@servicetitan/design-system';
import { getTestIdProps } from '@lib/testUtils';
import { mainLayoutLocators } from '@locators/main-layout';
import { formatUnreadCount } from '../lib/formatUnreadCount';
import { useUnreadTextCount } from '../lib/unreadTextCountContext';
import styles from './MessagesNavBadge.module.css';

/**
 * Team-wide unread text-conversation count on the Messages side-nav item.
 *
 * Extends the pattern of `UrgentFollowUpsBadge` -- same `Tag`, same
 * `marginLeft: 'auto'`, same `!!badgeText &&` conditional render -- but not the
 * component itself, whose props are follow-up-query-shaped.
 *
 * Two deliberate divergences from that sibling:
 * - the `99+` cap (it is uncapped);
 * - no error indicator. The sibling renders `'?'` with an error-titled tooltip
 *   on query failure; this badge renders nothing, because a `'?'` would break
 *   the "no badge when the count is unavailable" rule.
 *
 * There is deliberately no "count went stale" branch either. The shared registry
 * has no removal path and retains the last value, so a producer that dies
 * mid-session leaves the badge frozen and the host cannot detect it. Do not add
 * a staleness timer.
 *
 * No gate check here: the badge already renders inside `MainLayout`'s texting
 * flag ternary and its `RequirePermissions`, so a second check would be a
 * tautology.
 */
const MessagesNavBadge = () => {
  const count = useUnreadTextCount();
  const badgeText = formatUnreadCount(count);

  return (
    !!badgeText && (
      <Tag
        badge
        compact
        className={styles.sidenavBadge}
        color='critical'
        style={{ marginLeft: 'auto' }}
        title={`${count} unread messages`}
        {...getTestIdProps(mainLayoutLocators.sidenav.inboxMessagesBadgeTestId)}
      >
        {badgeText}
      </Tag>
    )
  );
};

export default MessagesNavBadge;
